import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';

import settings from '/@/core/settings';
import styles from './styles.module.css';

@autobind
export default class Select extends Component {
    static propTypes = {
        appearance: PropTypes.oneOf(['fill', 'outline']),
        size: PropTypes.oneOf(['sm', 'md', 'lg']),
        placeholder: PropTypes.any,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.any.isRequired,
            })
        ).isRequired,
        value: PropTypes.any,
        onChange: PropTypes.func.isRequired,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        persistPlaceholder: PropTypes.bool,
        clearable: PropTypes.bool,
        clearLabel: PropTypes.string,
        shadow: PropTypes.bool,
        disabled: PropTypes.bool,
        compact: PropTypes.bool,
        className: PropTypes.string,
        noWrap: PropTypes.bool,
        ellipsis: PropTypes.bool,
        title: PropTypes.string,
        titleStyles: PropTypes.string,
        error: PropTypes.bool,
        errorMsg: PropTypes.string,
        maxHeight: PropTypes.string,
        classNameOptions: PropTypes.string,
        classNameValue: PropTypes.string,
        colorValue: PropTypes.bool,
        bolder: PropTypes.bool,
    };

    static defaultProps = {
        appearance: 'outline',
        size: 'md',
        clearable: false,
        compact: false,
        noWrap: true,
        ellipsis: false,
        error: false,
        colorValue: false,
    };

    state = {
        focused: false,
        showOptions: false,
    };

    getSelectedIndex() {
        const index = this.props.options.findIndex(({ value }) => {
            if (typeof value !== 'object') return value === this.props.value;
            if (typeof value === 'object' && typeof this.props.value === 'object') {
                return Object.keys(value).reduce((result, key) => {
                    if (!result) return result;
                    return value[key] === this.props.value[key];
                }, true);
            }
        });
        return index >= 0 ? index : '';
    }

    getOptionKey(option) {
        return option.label + (option.value._id || option.value.id || option.value);
    }

    handleFocus() {
        if (this.props.disabled) return;
        this.setState({ focused: true });
        if (this.props.onFocus) this.props.onFocus();
    }

    handleBlur() {
        if (this.props.disabled) return;
        this.setState({ focused: false });
        if (this.props.onBlur) this.props.onBlur();
    }

    handleChange(index) {
        if (this.props.onChange) {
            if (index === '') {
                this.props.onChange(undefined);
            } else {
                const option = this.props.options[Number(index)];
                this.props.onChange(option.value, option);
            }
            this.handleClose();
        }
    }

    handleOpen() {
        if (this.props.disabled) return;
        this.setState({ showOptions: true });
    }

    handleClose() {
        this.setState({ showOptions: false });
    }

    renderValue() {
        const selectedOptionIndex = this.getSelectedIndex();
        const selectedOption = selectedOptionIndex !== '' && this.props.options[selectedOptionIndex];
        let value;
        if (!selectedOption) {
            return (value = <span>{this.props.placeholder}</span>);
        } else if (!this.props.persistPlaceholder) {
            value = (
                <span
                    className={classNames(
                        styles.value,
                        this.props.colorValue ? styles[`value-${settings.app.theme}`] : undefined
                    )}
                >
                    {selectedOption.label}
                </span>
            );
        } else {
            value = (
                <span>
                    {this.props.placeholder}:{' '}
                    <span
                        className={classNames(
                            styles.value,
                            this.props.colorValue ? styles[`value-${settings.app.theme}`] : undefined
                        )}
                    >
                        {selectedOption.label}
                    </span>
                </span>
            );
        }

        return (
            <div
                className={classNames(
                    styles.display,
                    { [styles.placeholder]: selectedOptionIndex === '' },
                    this.props.noWrap && styles.addNoWrap,
                    this.props.ellipsis && styles.ellipsisTwoLines
                )}
            >
                {value}
            </div>
        );
    }

    componentWillUnmount() {
        this.handleClose();
    }

    renderIconForSelect() {
        if (this.props.clearable)
            return this.props.value !== undefined ? (
                <i
                    onClick={(e) => {
                        e.stopPropagation();
                        this.handleChange('');
                    }}
                    className={classNames('material-icons', styles.close)}
                >
                    close
                </i>
            ) : (
                !this.props.compact && <i className={classNames('material-icons')}>keyboard_arrow_down</i>
            );
        return !this.props.compact && <i className={classNames('material-icons')}>keyboard_arrow_down</i>;
    }

    render() {
        const className = classNames(
            styles.root,
            this.props.className,
            styles[`appearance-${this.props.appearance}`],
            styles[`size-${this.props.size}`],
            this.props.bgColor && styles[`bg-color-${this.props.color}`],
            !this.props.bgColor && styles[`color-${this.props.color}`],
            this.props.disabled && styles.disabled,
            this.props.shadow && styles.shadow,
            this.state.focused && styles.focused,
            this.props.error && styles.error
        );

        return (
            <Fragment>
                {this.props.title && <div className={this.props.titleStyles}>{this.props.title}</div>}
                <div className={className} tabIndex='0' onBlur={this.handleClose}>
                    <div
                        onClick={this.handleOpen}
                        className={classNames(
                            styles.firstOption,
                            this.props.classNameValue,
                            this.props.bolder && styles.bolder
                        )}
                    >
                        {this.renderValue()}
                        {this.renderIconForSelect()}
                    </div>
                    {this.state.showOptions && (
                        <ul
                            className={classNames(styles.list, this.props.maxHeight && styles.maxHeight)}
                            style={{
                                maxHeight: this.props.maxHeight ? `${this.props.maxHeight}` : undefined,
                            }}
                        >
                            {this.props.options.map((option, index) => {
                                return (
                                    <li
                                        onClick={() => this.handleChange(index)}
                                        key={this.getOptionKey(option)}
                                        disabled={option.disabled}
                                        value={index}
                                        className={classNames(
                                            this.props.value === option.value &&
                                                styles[`selected-${settings.app.theme}`],
                                            this.props.classNameOptions
                                        )}
                                    >
                                        {option.label}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                {Boolean(this.props.error) && typeof this.props.errorMsg === 'string' && (
                    <div className={styles.errorMessage}>{this.props.errorMsg}</div>
                )}
            </Fragment>
        );
    }
}
